// Alta / edición de una cuenta del vertical: la cuenta ES una página `company`
// del brain (frontmatter: aliases, gong_domains, sf_account_id, country, status).

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useBrainPage, useBrainPages, useBrainWrite } from '@/hooks/useBrain'
import type { BrainPageMeta } from '@/types/brain'

// Client-side mirror of _shared/brain.ts slugifyBrain — used only to detect
// create-mode collisions before the write.
function slugifyClient(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'pagina'
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When set, the dialog edits this company page in-place (same slug). */
  editing?: BrainPageMeta | null
}

const SF_ID_RE = /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/ // 15 o 18 exactos, como tp-sf-sync

export function AddAccountDialog({ open, onOpenChange, editing }: Props) {
  const write = useBrainWrite()
  // On edit, load the FULL page: the curated content_md must survive the save
  // (editing just to paste an SF id must never wipe the page body).
  const { data: fullPage, isLoading: pageLoading, isError: pageError } = useBrainPage(open && editing ? editing.slug : null)
  const { data: existingCompanies } = useBrainPages({ pageType: 'company', limit: 100 })
  const [name, setName] = useState('')
  const [country, setCountry] = useState('')
  const [aliases, setAliases] = useState('')
  const [gongDomains, setGongDomains] = useState('')
  const [sfAccountId, setSfAccountId] = useState('')
  const [status, setStatus] = useState('')
  const [currency, setCurrency] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!open) return
    const fm = (editing?.frontmatter ?? {}) as Record<string, unknown>
    setName(editing?.title ?? '')
    setCountry(String(fm.country ?? ''))
    setAliases(Array.isArray(fm.aliases) ? (fm.aliases as string[]).join(', ') : '')
    setGongDomains(Array.isArray(fm.gong_domains) ? (fm.gong_domains as string[]).join(', ') : '')
    setSfAccountId(String(fm.sf_account_id ?? ''))
    setStatus(String(fm.status ?? ''))
    setCurrency(String(fm.currency ?? ''))
    setNotes('')
  }, [open, editing])

  // Prefill the content textarea ONCE per open (edit mode only) — a
  // window-refocus refetch must not wipe in-progress edits.
  const seededRef = useRef(false)
  useEffect(() => {
    if (!open) { seededRef.current = false; return }
    if (editing && fullPage && !seededRef.current) {
      seededRef.current = true
      setNotes(fullPage.content_md ?? '')
    }
  }, [open, editing, fullPage])

  const csv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

  const submit = async () => {
    if (!name.trim()) { toast.error('El nombre de la cuenta es obligatorio'); return }
    // A failed page fetch would make the save wipe the body — hard-block it.
    if (editing && (pageLoading || pageError || !fullPage)) {
      toast.error(pageError ? 'No se pudo cargar la página — cierra y reintenta' : 'Cargando la página — intenta en un segundo')
      return
    }
    const sfId = sfAccountId.trim()
    if (sfId && !SF_ID_RE.test(sfId)) {
      toast.error('El Salesforce Account Id debe ser el Id de 15–18 caracteres (no el nombre)')
      return
    }
    // Crear con un nombre que colisiona con una cuenta existente la
    // sobreescribiría (upsert por slug) — obligar a usar Editar.
    {
      // Colisión por slug O por título — también al renombrar (excluyendo la
      // propia página): dos cuentas con el mismo nombre desfilan el matching
      // de Gong hacia la equivocada.
      const candidate = `companies/${slugifyClient(name.trim())}`
      const nameLower = name.trim().toLowerCase()
      const clash = (existingCompanies ?? []).some((c) =>
        c.slug !== editing?.slug && (c.slug === candidate || c.title.trim().toLowerCase() === nameLower))
      if (clash) {
        toast.error('Ya existe una cuenta con ese nombre — ábrela con el botón Editar en su card')
        return
      }
    }
    // En edición, un contenido vacío es SIEMPRE intencional o un descuido —
    // nunca restaurar en silencio: pedir confirmación explícita.
    if (editing && !notes.trim()) {
      toast.error('El contenido de la página quedó vacío — escribe algo o cierra sin guardar')
      return
    }
    const fallbackContent = editing
      ? (fullPage?.content_md ?? '')
      : `Cuenta del vertical Tarjeta Presente.${country.trim() ? ` País: ${country.trim()}.` : ''}`
    try {
      const res = await write.mutateAsync({
        ...(editing ? { slug: editing.slug } : {}),
        page_type: 'company',
        title: name.trim(),
        content_md: notes.trim() || fallbackContent,
        frontmatter: {
          country: country.trim() || null,
          aliases: csv(aliases),
          gong_domains: csv(gongDomains).map((d) => d.toLowerCase().replace(/^@/, '')),
          sf_account_id: sfId || null,
          status: status.trim() || null,
          currency: currency.trim().toUpperCase() || null,
        },
        source: 'manual',
      })
      toast.success(`Cuenta guardada: ${res.slug}`)
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo guardar la cuenta')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Editar cuenta' : 'Agregar cuenta al vertical'}</DialogTitle>
          <DialogDescription>
            La cuenta se guarda como página del cerebro. Los dominios de correo conectan sus llamadas
            de Gong; el Account Id conecta su pipeline de Salesforce.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="tp-name">Nombre *</Label>
            <Input id="tp-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="BanCoppel" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="tp-country">País</Label>
              <Input id="tp-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="México" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="tp-status">Estado</Label>
              <Input id="tp-status" value={status} onChange={(e) => setStatus(e.target.value)} placeholder="En negociación" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tp-aliases">Aliases (coma-separados)</Label>
            <Input id="tp-aliases" value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="BanCoppel, Banco Coppel" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tp-domains">Dominios de correo para Gong (coma-separados)</Label>
            <Input id="tp-domains" value={gongDomains} onChange={(e) => setGongDomains(e.target.value)} placeholder="bancoppel.com, qualitas.com.mx" />
          </div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="tp-sfid">Salesforce Account Id</Label>
              <Input id="tp-sfid" value={sfAccountId} onChange={(e) => setSfAccountId(e.target.value)} placeholder="001Hu00002abcDEF" className="font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="tp-currency">Moneda deals</Label>
              <Input id="tp-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} placeholder="MXN" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tp-notes">{editing ? 'Contenido de la página (markdown)' : 'Contexto inicial (markdown, opcional)'}</Label>
            <Textarea
              id="tp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={editing ? 6 : 3}
              disabled={!!editing && pageLoading}
              placeholder={editing && pageLoading ? 'Cargando contenido…' : 'Dueños de sus terminales (EVO → Ingenico Q4 2026)…'}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={write.isPending}>
            {write.isPending ? 'Guardando…' : 'Guardar cuenta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
